import {
  PublicClientApplication,
  InteractionRequiredAuthError,
} from "@azure/msal-browser";
import { config } from "./config.js";

const SCOPES = ["User.Read", "Files.ReadWrite"];

let pca = null;

function redirectUri() {
  // Local Vite sync needs localhost callback; Azure SPA must list both URIs.
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") {
    return `${window.location.origin}/`;
  }
  const configured = (config.redirectUri || "").trim();
  if (configured) return configured.endsWith("/") ? configured : `${configured}/`;
  return window.location.origin + window.location.pathname.replace(/\/?$/, "/");
}

export function isConfigured() {
  return Boolean(config.clientId && !config.clientId.startsWith("YOUR_"));
}

export async function initAuth() {
  if (!isConfigured()) return null;
  const uri = redirectUri();
  pca = new PublicClientApplication({
    auth: {
      clientId: config.clientId,
      authority: config.authority,
      redirectUri: uri,
      postLogoutRedirectUri: uri,
    },
    cache: {
      cacheLocation: "localStorage",
    },
  });
  await pca.initialize();
  const result = await pca.handleRedirectPromise();
  if (result?.account) {
    pca.setActiveAccount(result.account);
  } else {
    const accounts = pca.getAllAccounts();
    if (accounts.length) pca.setActiveAccount(accounts[0]);
  }
  return pca;
}

export function getAccount() {
  return pca?.getActiveAccount() ?? null;
}

export async function login() {
  if (!pca) throw new Error("Auth not initialized");
  const req = { scopes: SCOPES, redirectUri: redirectUri(), prompt: "select_account" };
  if (config.loginHint) req.loginHint = config.loginHint;
  await pca.loginRedirect(req);
}

export async function logout() {
  if (!pca) return;
  const account = getAccount();
  await pca.logoutRedirect({
    account: account ?? undefined,
    postLogoutRedirectUri: redirectUri(),
  });
}

export async function getAccessToken() {
  if (!pca) throw new Error("Auth not initialized");
  const account = getAccount();
  if (!account) throw new Error("Not signed in");
  try {
    const result = await pca.acquireTokenSilent({
      account,
      scopes: SCOPES,
    });
    return result.accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      await pca.acquireTokenRedirect({
        scopes: SCOPES,
        account,
        redirectUri: redirectUri(),
      });
      return null;
    }
    throw err;
  }
}
